import type { IThemePageManagement } from '../types';

import { usePublishThemePage, useQueryThemeInfo, useRefetchThemePage } from '~/modules/apps';
import { buildQueryParamsInAppForV7, convertTemplateTypeToGPV7 } from '~/utils';
import { useHandleDuplicateThemePage } from '~/modules/campaign-template';
import { useGModalConfirm, useGModalDelete } from '~/presentation/common';
import { useLocationHandlers } from '~/hooks/useLocationHandlers';
import { useThemePageManagementContext } from '../contexts';
import { useCampaignNavigate } from '~/modules/campaign';
import { IThemePageStatus } from '~/types/themePage';
import { THEME_PAGE_MSG } from '../constants';
import { useToast } from '~/hooks/useToast';

const NEED_RE_PUBLISH_STATUS = [IThemePageStatus.PUBLISHED, IThemePageStatus.NEED_PUBLISHING];

interface IUseThemePageManagementActionsProps {
  themePage: IThemePageManagement;
  onHidePopover: () => void;
}

export const useThemePageManagementActions = ({
  themePage,
  onHidePopover,
}: IUseThemePageManagementActionsProps) => {
  const { id: themePageId, status, campaignInfo } = themePage;

  const { selectedItems, setSelectedItems, setDeletingThemePages, setShowDeleteFailBanner } =
    useThemePageManagementContext();

  const { handleOpenDeleteModal } = useGModalDelete();
  const { handleOpenModalConfirm } = useGModalConfirm();

  const { shopifyToast } = useToast();
  const { themeId } = useQueryThemeInfo();
  const { handlePublish } = usePublishThemePage();
  const { onEditCampaign } = useCampaignNavigate();
  const { refetch: refetchThemePages } = useRefetchThemePage();
  const { handleOpenPageGPInNewTab } = useLocationHandlers();
  const { handleDuplicateThemePage } = useHandleDuplicateThemePage();

  const convertedType = convertTemplateTypeToGPV7(themePage.type);

  const handleConvertToGemPages = () => {
    onHidePopover();
    setShowDeleteFailBanner(false);

    handleOpenModalConfirm({
      id: themePageId,
      onSuccess: () => {
        refetchThemePages();
        unselectItemAfterConvertOrDelete();

        const needRePublish = NEED_RE_PUBLISH_STATUS.includes(status);
        if (!needRePublish) return directToGPsAndHighlightNewThemePage();

        handlePublish(
          directToGPsAndHighlightNewThemePage,
          () => {
            shopifyToast(THEME_PAGE_MSG.PUBLISH.FAILED, { isError: true });
            directToGPsAndHighlightNewThemePage();
          },
          { themePageId },
        );
      },
    });
  };

  const handleDuplicateToGemPages = async () => {
    if (!convertedType) return;

    onHidePopover();
    setShowDeleteFailBanner(false);
    try {
      const duplicatedThemePage = await handleDuplicateThemePage({
        id: themePageId,
        type: convertedType,
        name: themePage.name,
        duplicateAsIndependent: true,
      });

      directToNewThemePageInGPs(duplicatedThemePage?.id);
    } catch (error) {
      shopifyToast(THEME_PAGE_MSG.DUPLICATE.FAILED, { isError: true });
    }
  };

  const handleOpenModalDelete = () => {
    onHidePopover();
    setShowDeleteFailBanner(false);
    setDeletingThemePages([themePage]);

    handleOpenDeleteModal({
      id: themePageId,
      name: 'delete template',
      onSuccess: unselectItemAfterConvertOrDelete,
      onClose: () => setDeletingThemePages([]),
    });
  };

  const handleViewExperiment = () => {
    if (!campaignInfo?.id) return;

    setShowDeleteFailBanner(false);
    onEditCampaign({ id: campaignInfo?.id, type: themePage.campaignInfo?.type }, { newTab: true });
  };

  return {
    handleDuplicateToGemPages,
    handleConvertToGemPages,
    handleOpenModalDelete,
    handleViewExperiment,
  };

  function unselectItemAfterConvertOrDelete() {
    if (selectedItems === 'All') return;
    setSelectedItems(selectedItems?.filter((id) => id !== themePageId));
  }

  function directToNewThemePageInGPs(newThemePageId?: string) {
    if (!themeId) return;

    const url = buildQueryParamsInAppForV7({
      pageId: newThemePageId ?? themePageId,
      version: 'v7',
      pageType: convertedType,
      type: 'gempagesv7',
      themeId: themeId,
    });

    handleOpenPageGPInNewTab(url);
  }

  function directToGPsAndHighlightNewThemePage() {
    handleOpenPageGPInNewTab(`app/pages?highlight_theme_page_id=${themePageId}`);
  }
};
